/**
 * Render a wallet failure as text a user can act on.
 *
 * CIP-30 wallets reject with plain objects, not Errors: `signData` with a
 * DataSignError `{ code, info }` and the general API with an APIError of the
 * same shape. `String(e)` on those prints "[object Object]", which is what a
 * real user was shown when their wallet's active account no longer matched
 * the connected address. Every arm here exists because some wallet actually
 * produced that shape.
 */

/** CIP-30 DataSignError codes (from the spec, and observed in the wild). */
const DATA_SIGN_HINTS: Record<number, string> = {
  1: "Your wallet couldn't produce this signature. If you're using a hardware wallet, data signing isn't supported — try a software wallet.",
  2: "Your wallet's active account doesn't match the connected address. Switch back to the account you connected (or deposited from) in your wallet, then try again.",
  3: "The signature request was declined in your wallet.",
};

export function describeWalletError(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  if (typeof e === "object" && e !== null) {
    const { code, info, message } = e as {
      code?: unknown;
      info?: unknown;
      message?: unknown;
    };
    const detail =
      typeof info === "string" && info.length > 0
        ? info
        : typeof message === "string" && message.length > 0
          ? message
          : undefined;
    if (typeof code === "number" && DATA_SIGN_HINTS[code]) {
      return detail
        ? `${DATA_SIGN_HINTS[code]} (${detail})`
        : DATA_SIGN_HINTS[code];
    }
    if (detail) return detail;
    try {
      const json = JSON.stringify(e);
      // "{}" means every field was unenumerable; the type name beats nothing.
      if (json && json !== "{}") return json;
    } catch {
      // Circular structure -- fall through to String.
    }
  }
  return String(e);
}
